import { conflicts } from '../data/conflicts';
import { categoryLabel, intensityColor } from '../utils';
import type { ConflictCategory } from '../types';

interface HeatmapCell {
  country: string;
  decade: string;
  score: number;
  conflicts: string[];
}

const decades = ['1940s', '1950s', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];

function decadeOf(year: number): string {
  const d = Math.floor(year / 10) * 10;
  return `${d}s`;
}

function catScore(cat: ConflictCategory): number {
  switch (cat) {
    case 'major_war': return 4;
    case 'minor_war': return 3;
    case 'conflict': return 2;
    case 'skirmish': return 1;
  }
}

export function renderHeatmapView(container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  // Build cells: for each country's primary entry, determine which decades they were active
  // A conflict spans from startYear to present (2026)
  const countryCells = new Map<string, Map<string, HeatmapCell>>();

  for (const c of conflicts) {
    const primary = c.countryNames[0];
    if (!countryCells.has(primary)) {
      countryCells.set(primary, new Map());
    }
    const cells = countryCells.get(primary)!;

    // For each decade the conflict spans
    for (let year = c.startYear; year <= 2026; year++) {
      const dec = decadeOf(year);
      if (!decades.includes(dec)) continue;
      const existing = cells.get(dec);
      const score = catScore(c.category);
      if (existing) {
        existing.score = Math.max(existing.score, score);
        if (!existing.conflicts.includes(c.name)) {
          existing.conflicts.push(c.name);
        }
      } else {
        cells.set(dec, { country: primary, decade: dec, score, conflicts: [c.name] });
      }
    }
  }

  // Sort countries by total score (most conflict at top)
  const countries = [...countryCells.entries()]
    .map(([name, cells]) => ({
      name,
      totalScore: [...cells.values()].reduce((s, c) => s + c.score, 0),
      cells,
    }))
    .sort((a, b) => b.totalScore - a.totalScore)
    .slice(0, 35); // Top 35 most conflict-affected

  const CELL_W = 70;
  const CELL_H = 24;
  const LABEL_W = 200;
  const PAD_TOP = 40;
  const W = LABEL_W + decades.length * CELL_W + 20;
  const H = PAD_TOP + countries.length * CELL_H + 20;

  wrapper.innerHTML = `
    <div class="view-header">
      <h2>Conflict Intensity Heatmap</h2>
      <p>Top 35 most conflict-affected countries by decade. Cell colour shows the maximum conflict intensity active during that decade.</p>
    </div>

    <div class="chart-panel" style="overflow-x:auto;">
      <h3>Countries × Decades</h3>
      <p class="chart-subtitle">Darker red = higher intensity. Hover for conflict names.</p>
      <svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="min-width:${W}px">
        <!-- Decade headers -->
        ${decades.map((d, i) => `
          <text x="${LABEL_W + i * CELL_W + CELL_W / 2}" y="${PAD_TOP - 10}" text-anchor="middle" class="heatmap-label" style="font-size:11px;font-weight:600">${d}</text>
        `).join('')}

        <!-- Rows -->
        ${countries.map((country, row) => {
          const y = PAD_TOP + row * CELL_H;
          return `
            <g>
              <text x="${LABEL_W - 8}" y="${y + CELL_H / 2 + 4}" text-anchor="end" class="heatmap-label" style="font-size:11px">${country.name}</text>
              ${decades.map((dec, col) => {
                const cell = country.cells.get(dec);
                const score = cell ? cell.score : 0;
                const fill = score > 0 ? intensityColor(score) : '#111827';
                const opacity = score > 0 ? 0.6 + score * 0.1 : 0.2;
                const tooltip = cell
                  ? `${country.name} (${dec}): ${categoryLabel((['skirmish', 'conflict', 'minor_war', 'major_war'] as const)[score - 1] || 'skirmish')}\n${cell.conflicts.join(', ')}`
                  : `${country.name} (${dec}): No active conflict`;
                return `
                  <rect class="heatmap-cell" x="${LABEL_W + col * CELL_W}" y="${y}" width="${CELL_W - 2}" height="${CELL_H - 2}" fill="${fill}" opacity="${opacity}" rx="2">
                    <title>${tooltip}</title>
                  </rect>
                `;
              }).join('')}
            </g>
          `;
        }).join('')}
      </svg>

      <div style="display:flex;gap:16px;margin-top:var(--space-lg);font-size:var(--font-size-sm);color:var(--text-secondary);">
        <span><span style="display:inline-block;width:14px;height:14px;border-radius:2px;background:${intensityColor(4)};vertical-align:middle;margin-right:4px;"></span> Major War</span>
        <span><span style="display:inline-block;width:14px;height:14px;border-radius:2px;background:${intensityColor(3)};vertical-align:middle;margin-right:4px;"></span> Minor War</span>
        <span><span style="display:inline-block;width:14px;height:14px;border-radius:2px;background:${intensityColor(2)};vertical-align:middle;margin-right:4px;"></span> Conflict</span>
        <span><span style="display:inline-block;width:14px;height:14px;border-radius:2px;background:${intensityColor(1)};vertical-align:middle;margin-right:4px;"></span> Skirmish</span>
        <span><span style="display:inline-block;width:14px;height:14px;border-radius:2px;background:#111827;vertical-align:middle;margin-right:4px;opacity:0.3;"></span> No conflict</span>
      </div>
    </div>
  `;
}
